import { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { getCookie } from 'cookies-next';
import { useFormik } from 'formik';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { object, string } from 'yup';

import { CONFIG } from '@/lib/config/config';

import { isEventAleadySet, pushAnalyticsEvent } from '../../lib/helpers/analytics/analytics.helper';
import { AssistantSignupComponent } from '../assistant/assistant-signup/assistant-signup.component';
import {
  ContentStyle,
  FormStyle,
  RootStyle,
  TermsAndConditions,
  TextLink
} from '../layout/layout.style';
import GridLoadingComponent from '../ui/grid/grid-loading.component';
import { useSignUpMutation } from './queries/mutations/use-signup.mutation';
import { useSignUpStore } from './useSignUp.store';

const AppSignUp = ({ language }) => {
  const { t } = useTranslation('auth');

  const signUpMutation = useSignUpMutation();
  const utmSourceCookie = getCookie('utm_source');
  const clickIDsCookie = getCookie('click_ids');
  let clickedIDsCookieParsed;
  if (typeof clickIDsCookie === 'string') {
    clickedIDsCookieParsed = JSON.parse(clickIDsCookie);
  }

  const [cbChecked, setCbChecked] = useState(false);

  const handleCbChange = () => {
    setCbChecked((oldValue) => !oldValue);
  };

  const usernameVMR = t('sign-up.validation.username.required', { ns: 'auth' });
  const passwordVMR = t('sign-up.validation.password.required', { ns: 'auth' });
  const emailVM = t('sign-up.validation.email.valid', { ns: 'auth' });
  const emailVMR = t('sign-up.validation.email.required', { ns: 'auth' });

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      language: language || 'en'
    },
    validationSchema: object({
      username: string().max(255).required(usernameVMR),
      email: string().email(emailVM).max(255).required(emailVMR),
      password: string().max(255).required(passwordVMR)
    }),
    onSubmit: (values, { setSubmitting }) => {
      const newValues = {
        ...values,
        sign_up_variant: 'SignUpC'
      };

      if (utmSourceCookie) {
        //@ts-ignore
        newValues.utm_source = utmSourceCookie;
      }
      if (clickedIDsCookieParsed) {
        //@ts-ignore
        newValues.click_ids = clickedIDsCookieParsed;
      }

      signUpMutation.mutate({ ...newValues, language });
      setSubmitting(false);
    }
  });
  useEffect(() => {
    if (!isEventAleadySet('sign_up_started')) {
      pushAnalyticsEvent({
        event: 'sign_up_started'
      });
    }
  }, []);
  return (
    <Box data-cy='signup-container' component='form' onSubmit={formik.handleSubmit}>
      <TextField
        error={Boolean(formik.touched.username && formik.errors.username)}
        fullWidth
        helperText={formik.touched.username && formik.errors.username}
        label={t('sign-up.username', { ns: 'auth' })}
        margin='normal'
        name='username'
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type='username'
        value={formik.values.username}
        variant='outlined'
        disabled={formik.isSubmitting}
        data-cy='sign-up-username-textfield'
      />
      <TextField
        error={Boolean(formik.touched.email && formik.errors.email)}
        fullWidth
        helperText={formik.touched.email && formik.errors.email}
        label={t('sign-up.email', { ns: 'auth' })}
        margin='normal'
        name='email'
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type='email'
        value={formik.values.email}
        variant='outlined'
        disabled={formik.isSubmitting}
        data-cy='sign-up-mail-textfield'
      />
      <TextField
        error={Boolean(formik.touched.password && formik.errors.password)}
        fullWidth
        helperText={formik.touched.password && formik.errors.password}
        label={t('sign-up.password', { ns: 'auth' })}
        margin='normal'
        name='password'
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        type='password'
        value={formik.values.password}
        variant='outlined'
        disabled={formik.isSubmitting}
        data-cy='sign-up-password-textfield'
      />
      <TermsAndConditions checked={cbChecked} handleChange={handleCbChange} />
      <Button
        type='submit'
        fullWidth
        disabled={
          formik.isSubmitting ||
          !cbChecked ||
          !formik.values.password ||
          !formik.values.username ||
          !formik.values.email ||
          (Object.keys(formik.errors).length === 0) === false
        }
        variant='contained'
        sx={{ mt: 3, mb: 4 }}
        data-cy='sign-up-submit-button'
      >
        {t('sign-up.submit', { ns: 'auth' })}
      </Button>
    </Box>
  );
};

const UserSignUp = () => {
  const {
    t,
    i18n: { language }
  } = useTranslation('auth');
  const router = useRouter();
  const succesCreateAccount = useSignUpStore((state) => state.succesCreateAccount);

  useEffect(() => {
    if (succesCreateAccount) {
      router.push(CONFIG.URL.SIGN_UP_ACCOUNT_CONFIRMATION.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [succesCreateAccount]);

  return (
    <RootStyle>
      <ContentStyle>
        <AssistantSignupComponent step='createAccount' />
        <FormStyle>
          {succesCreateAccount ? (
            <GridLoadingComponent />
          ) : (
            <Stack gap={2}>
              <Typography
                color='primary'
                variant='h3'
                data-cy='sign-up.headline'
                display='flex'
                justifyContent='center'
              >
                {t('sign-up.headline', { ns: 'auth' })}
              </Typography>

              <Typography variant='body2' display='flex' justifyContent='center'>
                <NextLink
                  href={router.pathname}
                  locale={language === 'en' ? 'de' : 'en'}
                  legacyBehavior
                >
                  {t('login.changeLanguage', { ns: 'auth' })}
                </NextLink>
              </Typography>

              <AppSignUp language={language} />
            </Stack>
          )}
        </FormStyle>
        {!succesCreateAccount && (
          <TextLink
            href={CONFIG.URL.LOGIN.path}
            title={t('sign-up.already-signed-up', { ns: 'auth' })}
          />
        )}
      </ContentStyle>
    </RootStyle>
  );
};

export default UserSignUp;
